import Box, { BoxProps } from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { observer } from 'mobx-react-lite'
import { themeConfig } from '@gap/shared/theme'
import useStore from 'reusable/mobx-store-utils'
import { PlatformContainer } from 'reusable/containers/PlatformContainer'
import Dialog from '@mui/material/Dialog'
import { useOptionalOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import Button from '@mui/material/Button'
import { useOptionalRouterStore } from 'features/main/main-router/org-guard/RouterStore'
import AccessDeniedStore, { AccessDeniedStoreProvider, useAccessDeniedStore } from './AccessDeniedStore'
import { useAuthStore } from 'auth/AuthStore'

const FullPageWrapper = observer((props: { children: React.ReactNode }) => {
   const authStore = useAuthStore()

   return <Dialog open={true} fullScreen={true}>
      <PlatformContainer contentCentered={true} includeProfileMenu={!authStore.isGuest}>
         {props.children}
      </PlatformContainer>
   </Dialog>
})

const EmptyWrapper = ((props: { children: React.ReactNode }) => <>{props.children}</>)

export type AccessDeniedType = 'default' | 'content-access-denied' | 'document-no-longer-available'

interface AccessDeniedProps {
   ParentWrapper?: React.ComponentType<{ children: React.ReactNode }> | null
   type?: AccessDeniedType
   sx?: BoxProps['sx']
}

export default observer(function AccessDenied(props: AccessDeniedProps) {
   const type = props.type ?? 'default'

   const orgGuardStore = useOptionalOrgGuardStore()
   const routerStore = useOptionalRouterStore()
   const accessDeniedStore = useStore(sp => new AccessDeniedStore(sp), { orgGuardStore, routerStore, type })
   // If parent wrapper is null, use EmptyWrapper. If it is set, use what that is. If it isnt set at all return default fullpage wrapper
   const Wrapper = ('ParentWrapper' in props && !props.ParentWrapper) ? EmptyWrapper : props.ParentWrapper || FullPageWrapper

   return <AccessDeniedStoreProvider store={accessDeniedStore}>
      <Wrapper>
         <Box
            sx={{
               width: '810px',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               margin: '0 auto',
               textAlign: 'center',
               gap: 3,
               ...props.sx
            }}
         >
            <accessDeniedStore.Icon sx={{ height: '25px', width: '25px', color: themeConfig.system.warning[600] }} />

            <accessDeniedStore.content />
         </Box>
      </Wrapper>
   </AccessDeniedStoreProvider>
})

export const AccessDeniedDefault = observer(function AccessDeniedDefault() {
   const accessDeniedStore = useAccessDeniedStore()
   return <>
      <Typography variant="h6" sx={{ color: themeConfig.primary[900] }}>
         We're sorry, but you don't have access to {accessDeniedStore.orgName}
      </Typography>

      <Typography variant="body1" sx={{ color: themeConfig.primary[900] }}>
         Please contact {accessDeniedStore.orgName} directly to request access{!accessDeniedStore.isClient ? <> or if you believe this is a mistake,{' '}
            <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={accessDeniedStore.goToSupport}>
               contact support
            </Box>.</> : <>.</>}
      </Typography>
   </>
})

export const ContentAccessDenied = observer(function ContentAccessDenied() {
   const accessDeniedStore = useAccessDeniedStore()

   return <>
      <Typography variant="h6" sx={{ color: themeConfig.primary[900] }}>
         You don't have permission to view this page or content
      </Typography>

      <Typography variant="body1" sx={{ color: themeConfig.primary[900], mt: 1 }}>
         Please contact an admin from your org to check or update your permissions
      </Typography>

      {accessDeniedStore.canGoHome && (
         <Button variant="contained" onClick={accessDeniedStore.goHome} sx={{ width: '200px', my: 6 }}>Go home</Button>
      )}
      <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
         If you think this is a mistake, please <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={accessDeniedStore.goToSupport}>contact support</Box>
      </Typography>
   </>
})

export const DocumentNoLongerAvailable = observer(function DocumentNoLongerAvailable() {
   const accessDeniedStore = useAccessDeniedStore()

   return <>
      <Typography variant="h6" sx={{ color: themeConfig.primary[900] }}>
         This document is no longer available
      </Typography>

      <Typography variant="body1" sx={{ color: themeConfig.primary[900], mt: 1 }}>
         The document you're looking for may have been removed or is no available for you to view.
      </Typography>

      {accessDeniedStore.canGoHome && (
         <Button variant="contained" onClick={accessDeniedStore.goHome} sx={{ width: '200px', my: 6 }}>Go home</Button>
      )}

      <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
         If you think this is a mistake, please <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={accessDeniedStore.goToSupport}>contact support</Box>
      </Typography>
   </>
})
